import { ApiResponse, authFetch } from './api.service';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

export interface TransformationRule {
  id: number | string;
  name: string;
  description?: string;
  sourceIngredientId: number | string;
  targetIngredientId: number | string;
  sourceQuantity: number;
  targetQuantity: number;
  conversionFactor: number;
  isActive: boolean;
  notes?: string;
  createdBy?: number | string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;

  // Related data populated by API - matching real API structure
  sourceIngredient?: {
    id: number;
    name: string;
    unitId: number;
    unitName: string;
    unitDisplayName: string;
    unitSymbol: string;
    categoryName: string;
  };
  targetIngredient?: {
    id: number;
    name: string;
    unitId: number;
    unitName: string;
    unitDisplayName: string;
    unitSymbol: string;
    categoryName: string;
  };
  creator?: {
    id: number;
    name: string;
  };

  // Computed properties for backward compatibility
  sourceUnit?: string;
  targetUnit?: string;
}

export interface CreateTransformationRuleData {
  name: string;
  description?: string;
  sourceIngredientId: string;
  targetIngredientId: string;
  sourceQuantity: number;
  sourceUnit: string;
  targetQuantity: number;
  targetUnit: string;
  conversionFactor: number;
  notes?: string;
}

export interface UpdateTransformationRuleData extends Partial<CreateTransformationRuleData> {
  isActive?: boolean;
}

class TransformationRulesService {
  /**
   * Get all transformation rules
   */
  async getTransformationRules(params: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    includeInactive?: boolean;
  } = {}): Promise<ApiResponse<TransformationRule[]>> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.includeInactive) queryParams.append('includeInactive', 'true');

    const url = `${API_BASE_URL}/transformation-rules${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await authFetch(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener reglas de transformación');
    }

    return await response.json();
  }

  /**
   * Get transformation rule by ID
   */
  async getTransformationRule(id: string): Promise<ApiResponse<TransformationRule>> {
    const response = await authFetch(`${API_BASE_URL}/transformation-rules/${id}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener regla de transformación');
    }

    return await response.json();
  }

  /**
   * Create new transformation rule
   */
  async createTransformationRule(data: CreateTransformationRuleData): Promise<ApiResponse<TransformationRule>> {
    const response = await authFetch(`${API_BASE_URL}/transformation-rules`, {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al crear regla de transformación');
    }

    return await response.json();
  }

  /**
   * Update transformation rule
   */
  async updateTransformationRule(id: string, data: UpdateTransformationRuleData): Promise<ApiResponse<TransformationRule>> {
    const response = await authFetch(`${API_BASE_URL}/transformation-rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al actualizar regla de transformación');
    }

    return await response.json();
  }

  /**
   * Delete transformation rule (soft delete)
   */
  async deleteTransformationRule(id: string): Promise<ApiResponse<void>> {
    const response = await authFetch(`${API_BASE_URL}/transformation-rules/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al eliminar regla de transformación');
    }

    return await response.json();
  }
}

export const transformationRulesService = new TransformationRulesService();